import {
    Action,
    ActionReducer,
    createFeature,
    createReducer,
    on,
} from '@ngrx/store';
import {
    loadWatchlist,
    loadWatchlistSuccess,
    loadWatchlistFailure,
    addToWatchlist,
    removeFromWatchlist,
    watchlistTotalLoaded,
    watchlistUserChanged,
} from './watchlist-page-state.actions';
import {
    GetMyWatchedItems200Response,
    WatchedItemsFlattenedListResponse,
} from '@viewbid/ts-angular-client';

export const watchlistPageFeatureKey: string = 'watchlist';

/**
 * Defines the state structure for the Watchlist Page.
 */
export interface WatchlistPageState {
    watchlist: GetMyWatchedItems200Response | undefined;
    loading: boolean;
    error: Error | null;
}

/**
 * Initial state for the WatchlistPageState.
 */
export const initialState: WatchlistPageState = {
    watchlist: undefined,
    loading: false,
    error: null,
};

/**
 * Reducer function for handling actions related to the Watchlist Page.
 */
export const watchlistReducer: ActionReducer<WatchlistPageState, Action> =
    createReducer(
        initialState,
        on(
            loadWatchlist,
            (state: WatchlistPageState): WatchlistPageState => ({
                ...state,
                loading: true,
                error: null,
            }),
        ),
        on(watchlistUserChanged, (): WatchlistPageState => initialState),
        on(
            watchlistTotalLoaded,
            (
                state: WatchlistPageState,
                { totalCount }: { totalCount: number },
            ): WatchlistPageState => ({
                ...state,
                watchlist: {
                    ...state.watchlist,
                    data: state.watchlist?.data ?? [],
                    totalCount,
                },
            }),
        ),
        on(
            loadWatchlistSuccess,
            (
                state: WatchlistPageState,
                { response }: { response: GetMyWatchedItems200Response },
            ): WatchlistPageState => ({
                ...state,
                loading: false,
                watchlist: response,
            }),
        ),
        on(
            loadWatchlistFailure,
            (
                state: WatchlistPageState,
                { error }: { error: Error },
            ): WatchlistPageState => ({
                ...state,
                loading: false,
                error: error,
            }),
        ),
        on(
            addToWatchlist,
            (
                state: WatchlistPageState,
                { watchlistId }: { watchlistId: string },
            ): any => {
                const isAlreadyInWatchlist: boolean = Boolean(
                    state.watchlist?.data.some(
                        (item: WatchedItemsFlattenedListResponse) =>
                            item.auctionLotId === watchlistId,
                    ),
                );

                return {
                    ...state,
                    watchlist: {
                        ...state.watchlist,
                        totalCount: isAlreadyInWatchlist
                            ? state.watchlist?.totalCount
                            : (state.watchlist?.totalCount ?? 0) + 1,
                    },
                };
            },
        ),
        on(
            removeFromWatchlist,
            (
                state: WatchlistPageState,
                { watchlistId }: { watchlistId: string },
            ): any => {
                const updatedData: WatchedItemsFlattenedListResponse[] =
                    state.watchlist?.data?.filter(
                        (item: WatchedItemsFlattenedListResponse) =>
                            item.auctionLotId !== watchlistId,
                    ) ?? [];

                return {
                    ...state,
                    watchlist: {
                        ...state.watchlist,
                        data: updatedData,
                        totalCount: Math.max(
                            (state.watchlist?.totalCount ?? 0) - 1,
                            0,
                        ),
                    },
                };
            },
        ),
    );

// eslint-disable-next-line
export const watchlistPageFeature = createFeature({
    name: watchlistPageFeatureKey,
    reducer: watchlistReducer,
});
